import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import Link from 'components/shared/link';
import DEFAULT_EASE from 'constants/default-ease';

import capterraLogo from './images/testimonials-cards-capterra-logo.svg';
import g2Logo from './images/testimonials-cards-g2-logo.svg';
import stars from './images/testimonials-cards-stars.svg';

const TestimonialsCards = ({ title, items, footerText, theme }) => {
  const [itemsWrapperRef, isItemsWrapperInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section className={clsx('safe-paddings', theme === 'gray' && 'pb-18 xl:pb-16 md:pb-22')}>
      <div className={clsx(theme === 'gray' && 'relative bg-gray-1 pt-30 xl:pt-20 md:pt-14')}>
        <LazyMotion features={domAnimation}>
          <div className="container relative z-10">
            <h2 className="text-center text-5xl font-bold leading-denser">{title}</h2>
            <ul
              className="grid-gap-x mt-20 columns-3 xl:mt-14 lg:columns-2 md:mt-10 md:columns-auto md:space-y-4"
              ref={itemsWrapperRef}
            >
              {items.map(({ text, authorImage, authorName, date, url }, index) => (
                <li className="mb-7 inline-block xl:mb-6 lg:mb-4 md:mb-0 md:block" key={index}>
                  <Link
                    className={clsx(
                      'relative block rounded-2xl border border-gray-4 p-5 transition-all duration-200 hover:border-gray-3',
                      theme === 'gray' && 'bg-gray-1 hover:bg-white',
                      theme === 'white' && 'hover:bg-gray-1'
                    )}
                    to={url}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <div
                      className="testimonials-cards-item-text text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                    <div className="mt-5 flex items-center">
                      <img
                        className="w-12 rounded-[20px]"
                        height={48}
                        width={48}
                        src={authorImage.url}
                        alt={authorName}
                        loading="lazy"
                      />
                      <span className="ml-4 text-base font-medium leading-none xl:ml-3.5">
                        {authorName}
                      </span>
                      <span className="ml-auto text-xs font-medium leading-none text-gray-8">
                        {date}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {theme === 'gray' && (
              <div className="translate-y-12 xl:translate-y-10 md:translate-y-12">
                <div className="card-shadow relative mx-auto mt-10 flex w-max flex-col items-center rounded-2xl bg-white py-8 px-10 xl:mt-9 lg:mt-12 md:mt-11 md:py-8 xs:w-full xs:max-w-[260px]">
                  <img
                    className="h-7 md:h-6"
                    width={214}
                    height={28}
                    src={stars}
                    alt="Stars"
                    loading="lazy"
                  />
                  <div className="mt-7 flex md:mt-5 xs:flex-col xs:items-center">
                    <div className="mr-8 flex items-center border-r border-gray-4 pr-8 md:mr-6 md:pr-6 xs:mb-4 xs:mr-0 xs:border-b xs:border-r-0 xs:pb-4 xs:pr-0">
                      <img
                        className="w-[190px] md:w-[164px]"
                        width={190}
                        height={44}
                        src={capterraLogo}
                        alt="Capterra"
                        loading="lazy"
                      />
                    </div>
                    <img
                      className="w-[70px] md:w-[60px]"
                      width={70}
                      height={70}
                      src={g2Logo}
                      alt="G2"
                      loading="lazy"
                    />
                  </div>
                  <div
                    className="with-link-primary-2 mt-7 text-center md:mt-5 xs:mx-auto xs:max-w-[150px]"
                    dangerouslySetInnerHTML={{ __html: footerText }}
                  />
                  <div className="absolute top-1/2 left-1/2 z-[-1] -translate-y-1/2 -translate-x-1/2">
                    <m.div
                      className="h-72 w-72 rounded-full bg-primary-1 xs:h-[300px] xs:w-[300px]"
                      initial={{ scale: 0 }}
                      animate={isItemsWrapperInView && { scale: 1 }}
                      transition={{ duration: 2, ease: DEFAULT_EASE }}
                      aria-hidden
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {theme === 'gray' && (
            <div className="absolute top-0 left-0 h-full w-full overflow-hidden">
              <svg
                className="absolute bottom-22 left-1/2 w-[2422px] -translate-x-1/2 2xl:w-[2000px] xl:bottom-18 xl:-left-80 xl:translate-x-0 lg:bottom-9"
                viewBox="0 0 2422 1070"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <m.path
                  d="M1 1C1 1 763.775 115.054 370.496 679.593C-22.7825 1244.13 2034.39 1055.92 2421 961.84"
                  stroke="#f25a5a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={isItemsWrapperInView && { pathLength: 1 }}
                  transition={{ duration: 3, ease: DEFAULT_EASE }}
                />
              </svg>
            </div>
          )}
        </LazyMotion>
      </div>
    </section>
  );
};

TestimonialsCards.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      authorImage: PropTypes.shape({ url: PropTypes.string.isRequired }).isRequired,
      authorName: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  footerText: PropTypes.string,
  theme: PropTypes.oneOf(['gray', 'white']).isRequired,
};

TestimonialsCards.defaultProps = {
  footerText: null,
};

export default TestimonialsCards;
